/** บัญชี (D-180) — ผังบัญชี, การกำหนดบัญชี, งวดบัญชี, เอกสารบัญชี, ต้นทุนผลิตต่อใบสั่ง — อ่านอย่างเดียว (เอกสารสร้างอัตโนมัติ) */

import { useState } from 'react';

import { RecordPanel } from '../components/RecordPanel';
import type { RecordColumn } from '../components/RecordTable';

export interface AccountingScreenProps {
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

interface Tab {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  /** ต้องกรอกโรงงาน + ช่วงวันที่ก่อนจึงเรียก (endpoint บังคับ) */
  readonly windowed: boolean;
  readonly columns: readonly RecordColumn[];
}

const TABS: readonly Tab[] = [
  {
    id: 'documents',
    label: 'เอกสารบัญชี',
    path: '/v1/accounting-documents',
    windowed: true,
    columns: [
      { key: 'posting_date', label: 'วันที่ผ่านรายการ' },
      { key: 'plant_code', label: 'โรงงาน' },
      { key: 'document_type', label: 'ประเภท' },
      { key: 'reference_doc_type', label: 'อ้างอิง' },
      { key: 'fiscal_year', label: 'ปี' },
      { key: 'period_no', label: 'งวด' },
      { key: 'status', label: 'สถานะ' },
    ],
  },
  {
    id: 'production-cost',
    label: 'ต้นทุนผลิตต่อใบสั่ง',
    path: '/v1/production-cost',
    windowed: true,
    columns: [
      { key: 'order_no', label: 'ใบสั่งผลิต' },
      { key: 'plant_code', label: 'โรงงาน' },
      { key: 'product_code', label: 'สินค้า' },
      { key: 'total_debit', label: 'เดบิต' },
      { key: 'total_credit', label: 'เครดิต' },
    ],
  },
  {
    id: 'accounts',
    label: 'ผังบัญชี',
    path: '/v1/chart-of-accounts',
    windowed: false,
    columns: [
      { key: 'account_code', label: 'รหัสบัญชี' },
      { key: 'name', label: 'ชื่อ' },
      { key: 'account_type', label: 'ประเภท' },
      { key: 'account_group', label: 'กลุ่ม' },
    ],
  },
  {
    id: 'determination',
    label: 'การกำหนดบัญชี',
    path: '/v1/account-determination',
    windowed: false,
    columns: [
      { key: 'movement_type', label: 'ประเภทการเคลื่อนไหว' },
      { key: 'debit_account_code', label: 'เดบิต' },
      { key: 'debit_account_name', label: 'ชื่อบัญชีเดบิต' },
      { key: 'credit_account_code', label: 'เครดิต' },
      { key: 'credit_account_name', label: 'ชื่อบัญชีเครดิต' },
    ],
  },
  {
    id: 'periods',
    label: 'งวดบัญชี',
    path: '/v1/fiscal-periods',
    windowed: false,
    columns: [
      { key: 'fiscal_year', label: 'ปี' },
      { key: 'period_no', label: 'งวด' },
      { key: 'status', label: 'สถานะ' },
    ],
  },
];

export function AccountingScreen({ apiBaseUrl, fetchImpl }: AccountingScreenProps) {
  const [tabId, setTabId] = useState('documents');
  const [plant, setPlant] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const tab = TABS.find((t) => t.id === tabId) ?? TABS[0]!;
  const ready = !tab.windowed || (plant.trim() !== '' && from !== '' && to !== '');
  const io = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };

  return (
    <div className="cap-page" data-testid="accounting">
      <div className="cap-page__head">
        <h1 className="cap-page__title">บัญชีและต้นทุน</h1>
      </div>
      <div className="cap-filters" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tabId}
            className={`cap-button ${t.id === tabId ? 'cap-button--primary' : 'cap-button--ghost'}`}
            onClick={() => setTabId(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab.windowed && (
        <div className="cap-filters">
          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              aria-label="รหัสโรงงาน"
              value={plant}
              onChange={(e) => setPlant(e.target.value.toUpperCase())}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ตั้งแต่วันที่</span>
            <input className="cap-input" type="date" aria-label="ตั้งแต่วันที่" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ถึงวันที่</span>
            <input className="cap-input" type="date" aria-label="ถึงวันที่" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      )}
      {ready ? (
        <RecordPanel
          key={tab.id}
          path={tab.path}
          params={tab.windowed ? { plant_code: plant, date_from: from, date_to: to } : {}}
          columns={tab.columns}
          testId={`accounting-${tab.id}`}
          {...io}
        />
      ) : (
        <p className="cap-notice__text" data-testid="accounting-need-window">
          กรอกโรงงานและช่วงวันที่ก่อน (ช่วงเอกสารบัญชีไม่เกิน 92 วัน)
        </p>
      )}
    </div>
  );
}
